import React, { useLayoutEffect, useRef } from 'react';
import { array } from 'prop-types';
import styled from 'styled-components';
import { resolve } from 'library/helpers/path-resolver';
import { Header5, Header6, BodyText } from 'library/components/text-elements';
import { withA11y } from 'util/hoc';
import MapPinSVG from 'library/icons/map-pin.svg';
import PhoneSVG from 'library/icons/phone.svg';
import LetterSVG from 'library/icons/letter.svg';
import WebSVG from 'library/icons/web.svg';

const StyledServiceProviderDetails = withA11y(styled.section`
  display: flex;
  flex-direction: column;
  margin: 1em;
  width: 100%;
  height: 100%;

  ${(props) => (props.isTabbing ? 'outline: none;' : '')}
`);

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  margin: 1.5em;
  height: 100%;
`;

const StyledDetail = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid lightgray;
  margin-bottom: 1em;
`;

const StyledTitle = styled(Header6)`
  color: ${(props) => props.theme.colors.blue200};
`;

const StyledInfo = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  > p,
  a {
    width: 50%;
    padding: 0.5em 0.5em 0.5em 0;
    word-wrap: break-word;

    @media (max-width: ${(props) => props.theme.screen.tablet}px) {
      width: 100%;
    }

    svg {
      width: 22px;
      height: 22px;
      margin-right: 0.5em;
      vertical-align: middle;
      color: ${(props) => props.theme.colors.blue200};
    }
  }
`;

const StyledButton = styled.button`
  color: ${(props) => props.theme.colors.blue600};
  background-color: ${(props) => props.theme.colors.yellow300};
  border-color: ${(props) => props.theme.colors.yellow300};
  font-weight: bold;
  font-size: 1em;
  text-transform: unset;
  padding: 0.5em;
  max-width: 200px;
  height: 48px;
  margin-top: auto;
`;

const StyledProviderLink = styled.a`
  text-decoration: none;
  font-weight: bold;
  word-wrap: break-word;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledHeader = styled(Header5)`
  margin-left: 1em;
`;

const ServiceProviderDetails = ({ serviceProviders }) => {
  const detailRef = useRef({});

  useLayoutEffect(() => {
    if (detailRef.current) {
      detailRef.current?.focus();
    }
  }, []);

  const renderResultsDetails = (result, i) => {
    const address = result.addresses?.length ? result.addresses[0] : null;

    return (
      <StyledDetail key={result.id} role="dialog" tabIndex={0}>
        <StyledTitle>{result.title}</StyledTitle>
        <Header6>{result.agency_name}</Header6>
        <StyledInfo>
          {address ? (
            <BodyText tabIndex={0}>
              <MapPinSVG />
              {address.line_1}
              {address.city}
              {address.state}
            </BodyText>
          ) : null}
          {result.website && (
            <StyledProviderLink
              tabIndex={0}
              href={result.website}
              target="_blank"
            >
              <WebSVG />
              {result.website}
            </StyledProviderLink>
          )}
          {result.phone_number && (
            <BodyText tabIndex={0}>
              <PhoneSVG />
              {result.phone_number}
            </BodyText>
          )}
          {result.email && (
            <BodyText tabIndex={0}>
              <LetterSVG />
              {result.email}
            </BodyText>
          )}
        </StyledInfo>
      </StyledDetail>
    );
  };

  return (
    <StyledServiceProviderDetails tabIndex={0}>
      <StyledHeader ref={detailRef}>
        {`${resolve(
          serviceProviders,
          '0/service_provider_category/name',
          ''
        )} Resources near you`}
      </StyledHeader>
      <StyledDetails>
        {serviceProviders?.map(renderResultsDetails)}
      </StyledDetails>
    </StyledServiceProviderDetails>
  );
};

ServiceProviderDetails.propTypes = {
  serviceProviders: array,
};

export default ServiceProviderDetails;
